import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeftRight, Eye, FileSpreadsheet, FileText, Loader2, Upload, Wallet } from 'lucide-react';
import Alert from '../common/Alert';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import SettlementFilterBar from '../common/SettlementFilterBar.js';
import SettlementModalShell from '../common/SettlementModalShell.js';
import CompanySettlementBatchView from '../common/CompanySettlementBatchView.js';
import CompanySettlementDueView from '../common/CompanySettlementDueView.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { filterExportButtonClass, filterExportPairClass, formInputClass, formSelectClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIndianDate, formatIstDateTime, getCurrentMonthRange } from '../../utils/localDate.js';
import { hasPaidAmountSettlementAccess, hasLocationHubBookingAccess } from '../../utils/networkMode.js';
import { useAuth } from '../../context/AuthContext.js';
import companySettlementService from '../../services/companySettlementService.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';
import CompanyPaidLines from './CompanyPaidLines.js';
import { PAYMENT_MODES, aiReviewLabel, statusBadge, statusLabel } from '../paid-amount-settlement/paidAmountSettlementUi.js';

const REQUEST_TABS = [
  { id: 'pending', label: 'Pending', statusParam: 'OCR_PENDING,HUMAN_REVIEW,PENDING' },
  { id: 'approved', label: 'Approved', statusParam: 'APPROVED' },
  { id: 'rejected', label: 'Rejected', statusParam: 'REJECTED' },
];

function cashAmountsToSettle(summary) {
  const pas = Number(summary?.paid?.amount_outstanding || 0);
  const twoP = Number(summary?.to_pay?.amount_outstanding || 0);
  // Cashbook due is already after loading, unloading, and commission.
  // Do not take unloading off that figure again.
  if (summary?.cashbook_balance != null && summary?.cashbook_balance !== '') {
    return { paid: Math.max(0, pas), toPay: Math.max(0, twoP) };
  }
  const leftover = Number(summary?.leftover_unloading || 0);
  return {
    paid: Math.max(0, pas - leftover),
    toPay: Math.max(0, twoP - Math.max(0, leftover - pas)),
  };
}

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const moneyText = (value) => {
  const amount = roundMoney(value);
  return amount > 0 ? String(amount) : '';
};

// One typed total. Cash handed over on this branch stays in the amount first.
// The holder's own dues use whatever is left.
function splitActualSettleAmount(total, { heldPaid = 0, heldToPay = 0, paidCap = 0, toPayCap = 0 } = {}) {
  let left = Math.max(0, roundMoney(total));
  const handPaidCap = Math.min(Math.max(0, heldPaid), Math.max(0, paidCap));
  const handToPayCap = Math.min(Math.max(0, heldToPay), Math.max(0, toPayCap));
  const ownPaidCap = Math.max(0, roundMoney(paidCap - handPaidCap));
  const ownToPayCap = Math.max(0, roundMoney(toPayCap - handToPayCap));
  const handPaid = Math.min(handPaidCap, left);
  left = roundMoney(left - handPaid);
  const handToPay = Math.min(handToPayCap, left);
  left = roundMoney(left - handToPay);
  const ownPaid = Math.min(ownPaidCap, left);
  left = roundMoney(left - ownPaid);
  const ownToPay = Math.min(ownToPayCap, left);
  return {
    paid: roundMoney(handPaid + ownPaid),
    toPay: roundMoney(handToPay + ownToPay),
  };
}

const paidRowsFromSummary = (summary) => {
  const items = summary?.paid?.items || [];
  const fromItems = [];
  items.forEach((item) => {
    const meta = {
      agent_user_id: item.agent_user_id,
      agent_name: item.agent_name,
    };
    (item.shipments || []).forEach((row) => {
      fromItems.push({ ...row, ...meta });
    });
  });
  if (fromItems.length) return fromItems;
  return summary?.paid?.shipments || [];
};

const dateParams = (fromDate, toDate) => {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  return params;
};

const AgentCompanySettlementPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const showPaid = hasPaidAmountSettlementAccess(user) && hasLocationHubBookingAccess(user);
  const month = getCurrentMonthRange();
  const [fromDate, setFromDate] = useState(() => searchParams.get('from_date') || month.fromDate);
  const [toDate, setToDate] = useState(() => searchParams.get('to_date') || month.toDate);
  const isHubAdmin = user?.role === 'Hub Admin';
  const [listTab, setListTab] = useState(() => (user?.role === 'Hub Admin' ? 'paid' : 'to_pay'));
  const [requestTab, setRequestTab] = useState('pending');
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [requestCounts, setRequestCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoffLoading, setHandoffLoading] = useState(false);
  const [handoffAgents, setHandoffAgents] = useState([]);
  const [handoffAgentId, setHandoffAgentId] = useState('');
  const [handoffAmount, setHandoffAmount] = useState('');
  const [handoffBalance, setHandoffBalance] = useState(0);
  const [handoffRemarks, setHandoffRemarks] = useState('');
  const [paidAmount, setPaidAmount] = useState('');
  const [toPayAmount, setToPayAmount] = useState('');
  const [actualAmount, setActualAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentRef, setPaymentRef] = useState('');
  const [remarks, setRemarks] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [destinationType, setDestinationType] = useState('branch');
  const [destinationHubId, setDestinationHubId] = useState('');
  const [settleHubs, setSettleHubs] = useState([]);
  const [adminSettleHubId, setAdminSettleHubId] = useState('');
  const [viewBatch, setViewBatch] = useState(null);
  const [viewDue, setViewDue] = useState(null);
  const [companyPayments, setCompanyPayments] = useState([]);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const params = dateParams(fromDate, toDate);
      const statusParam = listTab === 'requests'
        ? (REQUEST_TABS.find((t) => t.id === requestTab)?.statusParam || '')
        : '';
      // Sequential: cards summary first (shared cache), then batches.
      // Parallel open was stacking two heavy GETs × many users → timeouts.
      const sumRes = await companySettlementService.getSummary({ ...params, cards: 1 });
      const batchRes = await companySettlementService.listBatches({
        ...params,
        ...(statusParam ? { status: statusParam } : {}),
      });
      const data = sumRes?.data || {};
      setSummary(data);
      setBatches(batchRes?.data || []);
      setCompanyPayments(batchRes?.company_payments || []);
      setRequestCounts(batchRes?.counts || { pending: 0, approved: 0, rejected: 0, all: 0 });
      if (!silent) {
        const amounts = cashAmountsToSettle(data);
        const credit = Number(data.approved_expense_credit || 0);
        const still = data.expense_credit_applied ? 0 : credit;
        const paid = Math.max(0, amounts.paid - Math.min(amounts.paid, still));
        const toPay = Math.max(0, amounts.toPay - Math.max(0, still - amounts.paid));
        setPaidAmount(paid > 0 ? String(paid) : '');
        setToPayAmount(toPay > 0 ? String(toPay) : '');
        setActualAmount(moneyText(paid + toPay));
      }
    } catch (err) {
      if (!silent) {
        Alert.error(err.response?.data?.error || 'Failed to load settlement dues');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [fromDate, toDate, listTab, requestTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const expenseCredit = Number(summary?.approved_expense_credit || 0);
  const creditAlreadyApplied = Boolean(summary?.expense_credit_applied);
  const rawPasDue = Number(summary?.paid?.amount_outstanding || 0);
  const rawTwoPDue = Number(summary?.to_pay?.amount_outstanding || 0);
  const creditStillToApply = creditAlreadyApplied ? 0 : expenseCredit;
  const pasDue = Math.max(0, rawPasDue - Math.min(rawPasDue, creditStillToApply));
  const twoPDue = Math.max(0, rawTwoPDue - Math.max(0, creditStillToApply - rawPasDue));
  const commission = Number(summary?.unpaid_commission || 0);
  const loadingKept = Number(summary?.loading_kept || 0);
  const unloadingKept = Number(summary?.unloading_kept || 0);
  const leftoverUnloading = Number(summary?.leftover_unloading || 0);
  const settleAmounts = cashAmountsToSettle(summary);
  const paidCashDue = Math.max(0, settleAmounts.paid - Math.min(settleAmounts.paid, creditStillToApply));
  const toPayCashDue = Math.max(0, settleAmounts.toPay - Math.max(0, creditStillToApply - settleAmounts.paid));
  const rawNet = Number(summary?.net_settlement || 0);
  const net = rawNet < -0.009 ? rawNet : Math.max(0, rawNet - creditStillToApply);
  const hasOpenDue = pasDue > 0.009 || twoPDue > 0.009;
  const remitLabel = net < -0.009
    ? 'Company owes you'
    : (hasOpenDue || net > 0.009 ? 'You remit' : 'Settled');
  const companyPaidTotal = companyPayments.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const paidSeparately = Number(summary?.commission_paid_separately || 0);
  const pendingPaid = Boolean(summary?.paid?.pending_request_id);
  const pendingTwoP = (summary?.to_pay?.items || []).some((item) => item.pending_request_id);
  const paidShipments = paidRowsFromSummary(summary);
  const toPayItems = summary?.to_pay?.items || [];
  const paidColTotal = paidShipments.reduce((sum, row) => sum + Number(row.paid_amount || 0), 0);
  const paidRemittedTotal = paidShipments.reduce((sum, row) => sum + Number(row.paid_remitted_amount || 0), 0);
  const paidLoadingTotal = paidShipments.reduce((sum, row) => sum + Number(row.loading_kept || 0), 0);
  const paidOutstandingTotal = paidShipments.reduce((sum, row) => sum + Number(row.amount_outstanding || 0), 0);
  const paidTotal = Number(summary?.paid?.amount_paid ?? paidColTotal);
  const paidDisabled = !showPaid || pasDue <= 0 || pendingPaid;
  const toPayDisabled = twoPDue <= 0 || pendingTwoP;
  const remitted = Number(summary?.review?.approved_paid_amount || 0);
  const pendingReviewCount = Number(summary?.review?.pending_review_count || 0);
  const pendingReviewAmount = Number(summary?.review?.pending_review_amount || 0);
  const canSettle = (showPaid && pasDue > 0 && !pendingPaid) || (twoPDue > 0 && !pendingTwoP);
  const isAgent = user?.role === 'Agent';
  const bookBalance = Number(summary?.cashbook_balance || 0);
  const canHandOff = isAgent && bookBalance > 0.009 && !pendingPaid && !pendingTwoP;

  const viewTabs = useMemo(() => {
    const tabs = [
      { id: 'to_pay', label: 'To-Pay', count: toPayItems.length },
    ];
    if (showPaid) {
      tabs.unshift({ id: 'paid', label: 'Paid', count: paidShipments.length });
    }
    tabs.push({ id: 'requests', label: isHubAdmin ? 'Requests' : 'My requests', count: requestCounts.all || 0 });
    tabs.push({ id: 'company_paid', label: 'Company paid', count: companyPayments.length });
    return tabs;
  }, [showPaid, toPayItems.length, paidShipments.length, requestCounts.all, isHubAdmin, companyPayments.length]);

  const onFileChange = (e) => {
    const file = pickedFile(e);
    if (!file) return;
    const okTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!okTypes.includes(file.type)) {
      Alert.error('Please upload a JPG/PNG image screenshot');
      return;
    }
    setReceipt(file);
  };

  const resetForm = (hubId = adminSettleHubId) => {
    setReceipt(null);
    setPaymentRef('');
    setRemarks('');
    setPaymentMode('cash');
    setDestinationType('branch');
    setDestinationHubId(hubId ? String(hubId) : '');
    setPaidAmount(paidCashDue > 0 ? String(paidCashDue) : '');
    setToPayAmount(toPayCashDue > 0 ? String(toPayCashDue) : '');
    setActualAmount(moneyText(
      (paidDisabled ? 0 : paidCashDue) + (toPayDisabled ? 0 : toPayCashDue),
    ));
  };

  useEffect(() => {
    if (!showForm) return undefined;
    const fromServer = summary?.settle_to_hubs;
    if (Array.isArray(fromServer)) {
      setSettleHubs(fromServer);
      setDestinationType('branch');
      setDestinationHubId((current) => {
        if (fromServer.length === 1) return String(fromServer[0].id);
        if (fromServer.some((hub) => String(hub.id) === String(current))) return String(current);
        return '';
      });
      return undefined;
    }
    let cancelled = false;
    import('../../services/locationHubService.js').then(({ locationHubService }) =>
      locationHubService.getHubsForSelect()
    )
      .then((hubRes) => {
        if (cancelled) return;
        const list = hubRes?.data?.hubs || hubRes?.data || [];
        const hubs = Array.isArray(list) ? list : [];
        const branchHub = hubs.find(
          (hub) => String(hub.id) === String(user?.location_hub_id || '')
        );
        const allowedIds = Array.isArray(branchHub?.settlement_location_hub_ids) && branchHub.settlement_location_hub_ids.length
          ? branchHub.settlement_location_hub_ids.map(String)
          : (branchHub?.settlement_location_hub_id ? [String(branchHub.settlement_location_hub_id)] : []);
        const choices = hubs.filter((hub) => allowedIds.includes(String(hub.id)));
        setSettleHubs(choices);
        setDestinationType('branch');
        setDestinationHubId(choices.length === 1 ? String(choices[0].id) : '');
      })
      .catch(() => {
        if (!cancelled) setSettleHubs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showForm, user?.location_hub_id, summary]);

  const branchHandoffs = summary?.branch_handoffs || [];
  const hasBranchHandoff = branchHandoffs.length > 0;
  const heldPaid = branchHandoffs.reduce((sum, row) => sum + Number(row.paid || 0), 0);
  const heldToPay = branchHandoffs.reduce((sum, row) => sum + Number(row.to_pay || 0), 0);

  const applyActualAmount = (raw) => {
    setActualAmount(raw);
    const parts = splitActualSettleAmount(parseFloat(raw || '0') || 0, {
      heldPaid,
      heldToPay,
      paidCap: paidDisabled ? 0 : paidCashDue,
      toPayCap: toPayDisabled ? 0 : toPayCashDue,
    });
    setPaidAmount(moneyText(parts.paid));
    setToPayAmount(moneyText(parts.toPay));
  };

  const openSettle = () => {
    if (!canSettle) return;
    resetForm();
    setShowForm(true);
  };

  const openHandoff = async () => {
    if (!canHandOff) return;
    setShowHandoff(true);
    setHandoffRemarks('');
    setHandoffLoading(true);
    try {
      const res = await companySettlementService.listBranchHandoffAgents();
      const data = res?.data || {};
      const agents = data.agents || [];
      const balance = Number(data.cashbook_balance || 0);
      setHandoffAgents(agents);
      setHandoffBalance(balance);
      setHandoffAmount(balance > 0.009 ? String(balance) : '');
      setHandoffAgentId(agents.length === 1 ? String(agents[0].user_id) : '');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not load agents at your branch');
      setShowHandoff(false);
    } finally {
      setHandoffLoading(false);
    }
  };

  const onHandoff = async (e) => {
    e?.preventDefault?.();
    const amount = parseFloat(handoffAmount || '0') || 0;
    if (!handoffAgentId) {
      Alert.error('Select the agent at your branch');
      return;
    }
    if (amount <= 0) {
      Alert.error('Enter the amount to settle');
      return;
    }
    if (amount > handoffBalance + 0.01) {
      Alert.error('Amount is higher than the cash book');
      return;
    }
    try {
      setSubmitting(true);
      const res = await companySettlementService.settleToBranchAgent({
        to_user_id: handoffAgentId,
        amount,
        remarks: handoffRemarks,
      });
      Alert.success(res?.message || 'Cash book handed to the next agent');
      setShowHandoff(false);
      await loadData();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not settle to this agent');
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    const paid = paidDisabled ? 0 : (parseFloat(paidAmount || '0') || 0);
    const twoP = toPayDisabled ? 0 : (parseFloat(toPayAmount || '0') || 0);
    if (paid <= 0 && twoP <= 0) {
      Alert.error('Enter a Paid amount, a To-Pay amount, or both');
      return;
    }
    const hubToSend = settleHubs.length === 1
      ? String(settleHubs[0].id)
      : String(destinationHubId || '');
    if (!hubToSend) {
      Alert.error('Select the branch to settle to. Ask admin to set Settle to hubs on your location hub.');
      return;
    }
    if (pendingPaid && paid > 0) {
      Alert.error('A Paid settlement is already pending review');
      return;
    }
    if (pendingTwoP && twoP > 0) {
      Alert.error('A To-Pay settlement is already pending review');
      return;
    }
    try {
      setSubmitting(true);
      const form = new FormData();
      form.append('paid_amount', showPaid ? String(paid) : '0');
      form.append('to_pay_amount', String(twoP));
      form.append('payment_mode', paymentMode);
      form.append('payment_ref', paymentRef);
      form.append('remarks', remarks);
      if (receipt) form.append('receipt', receipt);
      form.append('destination_type', 'branch');
      form.append('destination_location_hub_id', hubToSend);
      const res = await companySettlementService.submitBatch(form);
      Alert.success(res?.message || 'Settlement submitted');
      setShowForm(false);
      resetForm();
      setListTab('requests');
      await loadData();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to submit settlement');
    } finally {
      setSubmitting(false);
    }
  };

  const exportParams = dateParams(fromDate, toDate);

  const downloadExcel = async () => {
    try {
      await companySettlementService.downloadExcel(exportParams);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Excel download failed');
    }
  };

  const downloadPdf = async () => {
    try {
      await companySettlementService.downloadPdf(exportParams);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'PDF download failed');
    }
  };

  const openView = async (rowOrId) => {
    const batchId = typeof rowOrId === 'string' ? rowOrId : rowOrId?.batch_id;
    if (!batchId) return;
    try {
      const res = await companySettlementService.getBatch(batchId);
      setViewDue(null);
      setViewBatch(res?.data || (typeof rowOrId === 'object' ? rowOrId : null));
    } catch (err) {
      if (typeof rowOrId === 'object') setViewBatch(rowOrId);
    }
  };

  const openDue = (kind, row) => {
    let pendingId = row?.pending_request_id || null;
    if (pendingId) {
      const match = batches.find((b) => (
        b.batch_id === pendingId
        || (b.paid_requests || []).some((r) => r.request_id === pendingId)
        || (b.to_pay_requests || []).some((r) => r.request_id === pendingId)
      ));
      if (match?.batch_id) pendingId = match.batch_id;
    }
    const pendingStatus = row.pending_request_status
      || (kind === 'paid' ? summary?.paid?.pending_request_status : null)
      || (kind === 'to_pay' ? summary?.to_pay?.pending_request_status : null);
    setViewDue({
      kind,
      row: {
        ...row,
        pending_request_id: pendingId,
        pending_request_status: pendingStatus,
      },
    });
  };

  const exportButtons = (
    <div>
      <label className="hidden lg:block text-xs font-medium text-transparent mb-1 select-none">Export</label>
      <div className={filterExportPairClass}>
        <Button variant="secondary" className={filterExportButtonClass} onClick={downloadExcel}>
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
        </Button>
        <Button variant="secondary" className={filterExportButtonClass} onClick={downloadPdf}>
          <FileText className="w-4 h-4 mr-1" /> PDF
        </Button>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Settle with Company"
        subtitle={
          isHubAdmin
            ? "Remit all agents at this hub, then track those requests here. Accountant approves — cashbook posts after approve."
            : "See Paid GCs and To-Pay MLs, remit together with one receipt. Accountant approves once — cashbook posts after approve."
        }
        icon={Wallet}
        backTo={isHubAdmin ? "/admin-dashboard" : "/agent-dashboard"}
      >
        {isAgent ? (
          <Button variant="secondary" icon={ArrowLeftRight} disabled={!canHandOff} onClick={openHandoff} mobileIconOnly>
            Settle to branch
          </Button>
        ) : null}
        <Button variant="primary" icon={Wallet} disabled={!canSettle} onClick={openSettle} mobileIconOnly>
          Make Settlement
        </Button>
      </ListPageHeader>

      <SettlementFilterBar
        statusLabel="View"
        tabs={viewTabs}
        activeTab={listTab}
        onTabChange={setListTab}
        fromDate={fromDate}
        toDate={toDate}
        onFromDate={setFromDate}
        onToDate={setToDate}
        extraRight={exportButtons}
      >
        {listTab === 'requests' ? (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <div className="flex flex-wrap gap-1.5 p-1 rounded-xl bg-gray-50 border border-gray-200">
              {REQUEST_TABS.map((tab) => {
                const active = requestTab === tab.id;
                const count = requestCounts[tab.id] ?? 0;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setRequestTab(tab.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
                      active
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/70'
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`min-w-[1.25rem] text-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                        active ? 'bg-blue-50 text-blue-700' : 'bg-gray-200/80 text-gray-600'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </SettlementFilterBar>

      {loading ? (
        <Loader fullScreen={false} message="Loading dues..." />
      ) : (
        <>
          <div className="-mx-0.5 mb-4 overflow-x-auto">
            <div className="flex min-w-full gap-1.5 sm:gap-3">
            {showPaid && (
              <div className="min-w-[5.75rem] flex-1 bg-white rounded-xl border border-amber-100 p-2 sm:p-3">
                <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">Total paid</p>
                <p className="text-sm sm:text-lg font-semibold text-amber-800 tabular-nums leading-tight">{fullCurrency(paidTotal)}</p>
              </div>
            )}
            <div className="min-w-[5.75rem] flex-1 bg-white rounded-xl border border-sky-100 p-2 sm:p-3">
              <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">To-Pay due</p>
              <p className="text-sm sm:text-lg font-semibold text-sky-800 tabular-nums leading-tight">{fullCurrency(twoPDue)}</p>
            </div>
            {hasOpenDue && twoPDue > 0.009 && commission > 0 && (
              <div className="min-w-[5.75rem] flex-1 bg-white rounded-xl border border-gray-100 p-2 sm:p-3">
                <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">Commission kept</p>
                <p className="text-sm sm:text-lg font-semibold text-gray-800 tabular-nums leading-tight">{fullCurrency(commission)}</p>
                <p className="text-[9px] sm:text-[10px] text-gray-500 mt-0.5 leading-tight">Already taken from To-Pay</p>
              </div>
            )}
            {hasOpenDue && showPaid && loadingKept > 0.009 && (
              <div className="min-w-[6.5rem] flex-1 bg-white rounded-xl border border-violet-100 p-2 sm:p-3">
                <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">Loading kept</p>
                <p className="text-sm sm:text-lg font-semibold text-violet-800 tabular-nums leading-tight">
                  {fullCurrency(loadingKept)}
                </p>
                <p className="text-[9px] sm:text-[10px] text-gray-500 mt-0.5 leading-tight">Booking agent</p>
              </div>
            )}
            {hasOpenDue && unloadingKept > 0.009 && (
              <div className="min-w-[6.5rem] flex-1 bg-white rounded-xl border border-violet-100 p-2 sm:p-3">
                <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">Unloading kept</p>
                <p className="text-sm sm:text-lg font-semibold text-violet-800 tabular-nums leading-tight">
                  {fullCurrency(unloadingKept)}
                </p>
                <p className="text-[9px] sm:text-[10px] text-gray-500 mt-0.5 leading-tight">
                  Delivery agent{leftoverUnloading > 0.009 ? ' (from delivery)' : ''}
                </p>
              </div>
            )}
            {expenseCredit > 0.009 ? (
              <div className="min-w-[6.5rem] flex-1 bg-sky-50 rounded-xl border border-sky-200 p-2 sm:p-3">
                <p className="text-[10px] sm:text-xs text-sky-800 leading-tight">Approved expense</p>
                <p className="text-sm sm:text-lg font-semibold text-sky-900 tabular-nums leading-tight">{fullCurrency(expenseCredit)}</p>
                <p className="text-[9px] sm:text-[10px] text-sky-700 mt-0.5 leading-tight">Taken off what you remit</p>
              </div>
            ) : null}
            <div className={`min-w-[5.75rem] flex-1 bg-white rounded-xl border p-2 sm:p-3 ${net < -0.009 ? 'border-emerald-300' : 'border-emerald-100'}`}>
              <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">{remitLabel}</p>
              <p className="text-sm sm:text-lg font-semibold text-emerald-800 tabular-nums leading-tight">{fullCurrency(Math.abs(net))}</p>
            </div>
            {paidSeparately > 0.009 ? (
              <div className="min-w-[5.75rem] flex-1 bg-white rounded-xl border border-emerald-300 p-2 sm:p-3">
                <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">Company owes you</p>
                <p className="text-sm sm:text-lg font-semibold text-emerald-800 tabular-nums leading-tight">{fullCurrency(paidSeparately)}</p>
                <p className="text-[9px] sm:text-[10px] text-gray-500 mt-0.5 leading-tight">Paid from Settlement Approvals</p>
              </div>
            ) : null}
            {isHubAdmin ? (
              <>
                <div className="min-w-[5.75rem] flex-1 bg-white rounded-xl border border-teal-100 p-2 sm:p-3">
                  <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">Remitted</p>
                  <p className="text-sm sm:text-lg font-semibold text-teal-800 tabular-nums leading-tight">{fullCurrency(remitted)}</p>
                </div>
                <div
                  className="min-w-[5.75rem] flex-1 bg-white rounded-xl border border-orange-100 p-2 sm:p-3 cursor-pointer"
                  onClick={() => setListTab('requests')}
                  onKeyDown={(e) => { if (e.key === 'Enter') setListTab('requests'); }}
                  role="button"
                  tabIndex={0}
                >
                  <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">Pending review</p>
                  <p className="text-sm sm:text-lg font-semibold text-orange-800 tabular-nums leading-tight">{fullCurrency(pendingReviewAmount)}</p>
                  <p className="text-[9px] sm:text-[10px] text-gray-500 mt-0.5 leading-tight">
                    {pendingReviewCount} request{pendingReviewCount === 1 ? '' : 's'}
                  </p>
                </div>
              </>
            ) : null}
            </div>
          </div>

          {companyPaidTotal > 0.009 && listTab !== 'company_paid' ? (
            <p className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              Company paid you {fullCurrency(companyPaidTotal)} in this period. Open Company paid to see it.
            </p>
          ) : null}

          {listTab === 'paid' && showPaid && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">Paid remittance due</h2>
              </div>
              {!paidShipments.length ? (
                <p className="text-sm text-gray-400 text-center py-8">No outstanding Paid GCs</p>
              ) : (
                <>
                <div className="hidden xl:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-semibold">GC</th>
                        {isHubAdmin ? (
                          <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                        ) : null}
                        <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Route</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Paid</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Remitted</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Loading kept</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Outstanding</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paidShipments.map((row) => (
                        <tr
                          key={row.shipment_id}
                          className="hover:bg-sky-50/50 cursor-pointer"
                          onClick={() => openDue('paid', { ...row, pending_request_id: pendingPaid ? summary?.paid?.pending_request_id : null, pending_request_status: summary?.paid?.pending_request_status })}
                        >
                          <td className="px-4 py-2.5 font-medium">{row.shipment_id}</td>
                          {isHubAdmin ? (
                            <td className="px-4 py-2.5 text-gray-600">
                              {row.agent_name || row.agent_user_id || '—'}
                            </td>
                          ) : null}
                          <td className="px-4 py-2.5 text-gray-600">{formatIndianDate(row.booking_date)}</td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {[row.sender_location, row.receiver_location].filter(Boolean).join(' → ') || '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.paid_amount)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-green-700">
                            {fullCurrency(row.paid_remitted_amount)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-violet-700">
                            {fullCurrency(row.loading_kept)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-red-700">
                            {fullCurrency(row.amount_outstanding)}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600 capitalize">{row.paid_remit_status || 'pending'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t border-gray-200">
                      <tr className="font-semibold text-gray-800">
                        <td className="px-4 py-2.5" colSpan={isHubAdmin ? 4 : 3}>Total</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(paidColTotal)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-green-700">{fullCurrency(paidRemittedTotal)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-violet-700">{fullCurrency(paidLoadingTotal)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-red-700">{fullCurrency(paidOutstandingTotal)}</td>
                        <td className="px-4 py-2.5" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="xl:hidden space-y-3 p-3">
                  {paidShipments.map((row) => (
                    <button
                      key={`card-${row.shipment_id}`}
                      type="button"
                      className="w-full text-left rounded-xl border border-gray-100 border-l-4 border-l-amber-400 bg-white p-3.5 shadow-sm"
                      onClick={() => openDue('paid', { ...row, pending_request_id: pendingPaid ? summary?.paid?.pending_request_id : null, pending_request_status: summary?.paid?.pending_request_status })}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm break-all min-w-0">{row.shipment_id}</p>
                        <p className="tabular-nums font-semibold text-red-700 shrink-0">{fullCurrency(row.amount_outstanding)}</p>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {formatIndianDate(row.booking_date)} · {[row.sender_location, row.receiver_location].filter(Boolean).join(' → ') || '—'}
                        {isHubAdmin && (row.agent_name || row.agent_user_id)
                          ? ` · ${row.agent_name || row.agent_user_id}`
                          : ''}
                      </p>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                        <span>Paid <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.paid_amount)}</span></span>
                        <span>Remitted <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.paid_remitted_amount)}</span></span>
                      </div>
                    </button>
                  ))}
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3 text-xs text-gray-700">
                    <div className="flex justify-between gap-2">
                      <span>Total paid</span>
                      <span className="tabular-nums font-semibold text-gray-900">{fullCurrency(paidColTotal)}</span>
                    </div>
                    <div className="flex justify-between gap-2 mt-1">
                      <span>Loading kept</span>
                      <span className="tabular-nums font-semibold text-violet-700">{fullCurrency(paidLoadingTotal)}</span>
                    </div>
                    <div className="flex justify-between gap-2 mt-1">
                      <span>Outstanding</span>
                      <span className="tabular-nums font-semibold text-red-700">{fullCurrency(paidOutstandingTotal)}</span>
                    </div>
                  </div>
                </div>
                </>
              )}
            </div>
          )}

          {listTab === 'to_pay' && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">To-Pay remittance due</h2>
              </div>
              {!toPayItems.length ? (
                <p className="text-sm text-gray-400 text-center py-8">No outstanding To-Pay MLs</p>
              ) : (
                <>
                <div className="hidden xl:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-semibold">ML Sheet</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Location</th>
                        {isHubAdmin ? (
                          <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                        ) : null}
                        <th className="text-right px-4 py-2.5 font-semibold">Total freight</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Commission</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Unloading kept</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Net due</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Returned</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {toPayItems.map((row) => (
                        <tr
                          key={`${row.ml_sheet_id}-${row.agent_user_id}`}
                          className="hover:bg-sky-50/50 cursor-pointer"
                          onClick={() => openDue('to_pay', row)}
                        >
                          <td className="px-4 py-2.5 font-medium">{row.ml_sheet_id}</td>
                          <td className="px-4 py-2.5 text-gray-600">{formatIndianDate(row.created_at)}</td>
                          <td className="px-4 py-2.5 text-gray-600">{row.location_name || '—'}</td>
                          {isHubAdmin ? (
                            <td className="px-4 py-2.5 text-gray-600">
                              {row.agent_name || row.agent_user_id || '—'}
                            </td>
                          ) : null}
                          <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.total_freight)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                            {row.commission_pct != null ? `${row.commission_pct}% · ` : ''}
                            {fullCurrency(row.commission_amount)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-violet-700">
                            {fullCurrency(row.unloading_kept)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.amount_due)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-green-700">
                            {fullCurrency(row.amount_returned)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-red-700">
                            {fullCurrency(row.amount_outstanding)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="xl:hidden space-y-3 p-3">
                  {toPayItems.map((row) => (
                    <button
                      key={`card-${row.ml_sheet_id}-${row.agent_user_id}`}
                      type="button"
                      className="w-full text-left rounded-xl border border-gray-100 border-l-4 border-l-sky-400 bg-white p-3.5 shadow-sm"
                      onClick={() => openDue('to_pay', row)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm break-all min-w-0">{row.ml_sheet_id}</p>
                        <p className="tabular-nums font-semibold text-red-700 shrink-0">{fullCurrency(row.amount_outstanding)}</p>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {formatIndianDate(row.created_at)} · {row.location_name || '—'}
                        {isHubAdmin && (row.agent_name || row.agent_user_id)
                          ? ` · ${row.agent_name || row.agent_user_id}`
                          : ''}
                      </p>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                        <span>Freight <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.total_freight)}</span></span>
                        <span>Commission <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.commission_amount)}</span></span>
                        <span>Net due <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.amount_due)}</span></span>
                        <span>Returned <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.amount_returned)}</span></span>
                      </div>
                    </button>
                  ))}
                </div>
                </>
              )}
            </div>
          )}

          {listTab === 'company_paid' && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">Company paid you</h2>
                <p className="text-xs text-gray-500 mt-0.5">One payment can cover several amounts. Open the receipt to see the image the company attached.</p>
              </div>
              {!companyPayments.length ? (
                <p className="text-sm text-gray-400 text-center py-10">No company payment in this period</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-semibold">Payment</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Mode</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Paid by</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {companyPayments.map((row) => (
                        <tr key={row.expense_id}>
                          <td className="px-4 py-2.5 font-mono text-xs">{row.expense_id}</td>
                          <td className="px-4 py-2.5">{formatIstDateTime(row.payment_date || row.expense_date) || '—'}</td>
                          <td className="px-4 py-2.5">{row.payment_mode || '—'}</td>
                          <td className="px-4 py-2.5">{row.paid_by || '—'}</td>
                          <td className="px-4 py-2.5 text-right">
                            <CompanyPaidLines row={row} align="right" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {listTab === 'requests' && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">
                  {isHubAdmin ? 'Settlement requests' : 'My requests'}
                </h2>
              </div>
              {!batches.length ? (
                <p className="text-sm text-gray-400 text-center py-10">No settlement requests yet</p>
              ) : (
                <>
                <div className="hidden xl:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-semibold">Batch</th>
                        {isHubAdmin ? (
                          <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                        ) : null}
                        <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                        <th className="text-left px-4 py-2.5 font-semibold">AI review</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Paid</th>
                        <th className="text-right px-4 py-2.5 font-semibold">To-Pay</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Total</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Submitted</th>
                        <th className="text-right px-4 py-2.5 font-semibold">View</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {batches.map((row) => {
                        const ocrStatus = (row.paid_requests?.[0] || row.to_pay_requests?.[0])?.ocr_status;
                        return (
                        <tr
                          key={row.batch_id}
                          className="hover:bg-sky-50/50 cursor-pointer"
                          onClick={() => openView(row)}
                        >
                          <td className="px-4 py-2.5 font-mono text-xs">
                            <button type="button" className="underline" onClick={(e) => { e.stopPropagation(); openView(row); }}>
                              {row.batch_id}
                            </button>
                          </td>
                          {isHubAdmin ? (
                            <td className="px-4 py-2.5 text-gray-700">{row.agent_name || row.agent_user_id || '—'}</td>
                          ) : null}
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs ${statusBadge(row.status)}`}>
                              {statusLabel(row.status)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">
                            {row.status === 'OCR_PENDING' ? 'AI reading…' : aiReviewLabel(ocrStatus)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.paid_amount)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.to_pay_amount)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fullCurrency(row.total_amount)}</td>
                          <td className="px-4 py-2.5 text-gray-500">{formatIstDateTime(row.submitted_at)}</td>
                          <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                            <Button variant="secondary" size="sm" onClick={() => openView(row)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="xl:hidden space-y-3 p-3">
                  {batches.map((row) => {
                    const ocrStatus = (row.paid_requests?.[0] || row.to_pay_requests?.[0])?.ocr_status;
                    return (
                      <button
                        key={`card-${row.batch_id}`}
                        type="button"
                        className="w-full text-left rounded-xl border border-gray-100 border-l-4 border-l-blue-500 bg-white p-3.5 shadow-sm"
                        onClick={() => openView(row)}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className={`${listIdLinkMobileClass} break-all`}>{row.batch_id}</span>
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold shrink-0 ${statusBadge(row.status)}`}>
                            {statusLabel(row.status)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500 block">Paid</span>
                            <p className="tabular-nums font-medium">{fullCurrency(row.paid_amount)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 block">To-Pay</span>
                            <p className="tabular-nums font-medium">{fullCurrency(row.to_pay_amount)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 block">Total</span>
                            <p className="tabular-nums font-semibold">{fullCurrency(row.total_amount)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 block">Submitted</span>
                            <p className="font-medium text-gray-800">{formatIstDateTime(row.submitted_at)}</p>
                          </div>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-2">
                          {row.status === 'OCR_PENDING' ? 'AI reading…' : aiReviewLabel(ocrStatus)}
                        </p>
                      </button>
                    );
                  })}
                </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {showHandoff ? (
        <SettlementModalShell
          title="Settle to branch"
          subtitle="Hand this cash book to the next agent on your branch. This stays inside the branch. The agent who still holds the cash settles it to the company."
          size="md"
          onClose={() => { if (!submitting) setShowHandoff(false); }}
          footer={(
            <div className="flex justify-end gap-2 flex-wrap">
              <Button variant="secondary" disabled={submitting} onClick={() => setShowHandoff(false)}>Cancel</Button>
              <Button variant="primary" disabled={submitting || handoffLoading} onClick={onHandoff}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Settle to agent
              </Button>
            </div>
          )}
        >
          {handoffLoading ? (
            <div className="py-8 flex justify-center"><Loader /></div>
          ) : (
            <form onSubmit={onHandoff} className="space-y-4">
              <div className="rounded-lg border border-sky-100 bg-sky-50/70 p-3 text-sm">
                <p className="text-xs text-gray-600">Cash book to hand over</p>
                <p className="font-semibold text-gray-900 tabular-nums">{fullCurrency(handoffBalance)}</p>
              </div>
              {!handoffAgents.length ? (
                <p className="text-sm text-amber-800">No other agent is active at your branch.</p>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Agent at your branch</label>
                  <select
                    value={handoffAgentId}
                    onChange={(e) => setHandoffAgentId(e.target.value)}
                    className={formSelectClass()}
                  >
                    {handoffAgents.length > 1 ? <option value="">Select agent</option> : null}
                    {handoffAgents.map((agent) => (
                      <option key={agent.user_id} value={String(agent.user_id)}>
                        {agent.name || agent.user_id}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={handoffAmount}
                  onChange={(e) => setHandoffAmount(e.target.value)}
                  className={formInputClass()}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
                <input
                  type="text"
                  value={handoffRemarks}
                  onChange={(e) => setHandoffRemarks(e.target.value)}
                  className={formInputClass()}
                  placeholder="Shift handoff"
                />
              </div>
            </form>
          )}
        </SettlementModalShell>
      ) : null}

      {showForm ? (
        <SettlementModalShell
          title="Make settlement"
          subtitle="One receipt for Paid and To-Pay. Submit goes to accountant review — cashbook posts after approve."
          onClose={() => setShowForm(false)}
          footer={(
            <div className="flex justify-end gap-2 flex-wrap">
              <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button variant="primary" disabled={submitting} onClick={onSubmit}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Submit settlement
              </Button>
            </div>
          )}
        >
          <form onSubmit={onSubmit} className="space-y-4">
            {expenseCredit > 0.009 ? (
              <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-950">
                <p className="text-xs font-semibold text-sky-800">Approved expense</p>
                <p className="mt-1 font-semibold tabular-nums">{fullCurrency(expenseCredit)} is deducted from this settlement.</p>
                {(summary?.approved_expense_lines || []).length ? (
                  <ul className="mt-2 space-y-1 text-xs">
                    {(summary.approved_expense_lines || []).map((line) => (
                      <li key={line.expense_id} className="flex justify-between gap-3">
                        <span className="break-all">{line.expense_id}</span>
                        <span className="shrink-0 font-semibold tabular-nums">{fullCurrency(line.amount)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            {hasBranchHandoff ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-xs text-gray-600">Actual amount settling</p>
                <p className="text-lg font-semibold text-emerald-800 tabular-nums">
                  {fullCurrency(
                    (paidDisabled ? 0 : Number.parseFloat(paidAmount || '0') || 0)
                    + (toPayDisabled ? 0 : Number.parseFloat(toPayAmount || '0') || 0),
                  )}
                </p>
                {(loadingKept > 0.009 || unloadingKept > 0.009) ? (
                  <p className="mt-1 text-xs text-gray-600">
                    Already subtracted
                    {showPaid && loadingKept > 0.009 ? `: loading kept ${fullCurrency(loadingKept)}` : ''}
                    {unloadingKept > 0.009
                      ? `${showPaid && loadingKept > 0.009 ? ',' : ':'} unloading kept ${fullCurrency(
                          leftoverUnloading > 0.009 ? leftoverUnloading : unloadingKept,
                        )}`
                      : ''}
                    .
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="rounded-lg border border-sky-100 bg-sky-50/70 p-3 text-sm grid grid-cols-1 sm:grid-cols-2 gap-3">
                {showPaid && (
                  <div>
                    <p className="text-xs text-gray-600">Company due (Paid)</p>
                    <p className="font-semibold text-gray-900 tabular-nums">{fullCurrency(pasDue)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-600">Company due (To-Pay)</p>
                  <p className="font-semibold text-gray-900 tabular-nums">{fullCurrency(twoPDue)}</p>
                </div>
                {(loadingKept > 0.009 || unloadingKept > 0.009) && (
                  <div className="sm:col-span-2 text-xs text-gray-600">
                    Already subtracted
                    {showPaid && loadingKept > 0.009
                      ? `: loading kept ${fullCurrency(loadingKept)} from Paid`
                      : ''}
                    {unloadingKept > 0.009
                      ? `${showPaid && loadingKept > 0.009 ? ',' : ':'} unloading kept ${fullCurrency(
                          leftoverUnloading > 0.009 ? leftoverUnloading : unloadingKept,
                        )} from To-Pay`
                      : ''}
                    . Send {fullCurrency(Math.abs(net))}.
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {hasBranchHandoff ? (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Actual amount settling</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={actualAmount}
                    onChange={(e) => applyActualAmount(e.target.value)}
                    className={formInputClass()}
                  />
                </div>
              ) : (
                <>
              {showPaid && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Actual Paid amount settling</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    className={`${formInputClass()} ${paidDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                    disabled={paidDisabled}
                    placeholder={pasDue <= 0 ? 'No Paid due' : undefined}
                  />
                  {pasDue <= 0 ? (
                    <p className="text-xs text-gray-500 mt-1">No Paid due — this field is disabled.</p>
                  ) : pendingPaid ? (
                    <p className="text-xs text-amber-700 mt-1">A Paid settlement is already pending review.</p>
                  ) : null}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Actual To-Pay amount settling</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                    value={toPayAmount}
                  onChange={(e) => setToPayAmount(e.target.value)}
                  className={`${formInputClass()} ${toPayDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                  disabled={toPayDisabled}
                  placeholder={twoPDue <= 0 ? 'No To-Pay due' : undefined}
                />
                {twoPDue <= 0 ? (
                  <p className="text-xs text-gray-500 mt-1">No To-Pay due — this field is disabled.</p>
                ) : pendingTwoP ? (
                  <p className="text-xs text-amber-700 mt-1">A To-Pay settlement is already pending review.</p>
                ) : null}
              </div>
                </>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Settle to branch
                </label>
                <select
                  value={destinationHubId}
                  onChange={(e) => {
                    setDestinationType('branch');
                    setDestinationHubId(e.target.value);
                  }}
                  className={formSelectClass()}
                  disabled={settleHubs.length <= 1}
                >
                  {settleHubs.length === 0 && <option value="">No settlement hub set</option>}
                  {settleHubs.length > 1 && <option value="">Select branch</option>}
                  {sortLocationsAsc(settleHubs).map((hub) => (
                    <option key={hub.id} value={String(hub.id)}>
                      {formatLocationDropdownLabel(hub) || hub.label || hub.hub_id || hub.id}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {settleHubs.length === 0
                    ? 'Your location hub has no settle hub. Ask admin to tick one on the location hub.'
                    : settleHubs.length === 1
                      ? 'Your branch settles to this hub.'
                      : 'Your branch allows these hubs. Choose one. The full amount goes to that hub.'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment mode</label>
                <select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className={formSelectClass()}
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reference</label>
                <input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  className={formInputClass()}
                />
              </div>
            </div>
            {!hasBranchHandoff && (Number(paidAmount || 0) > 0 || Number(toPayAmount || 0) > 0) && (
              <p className="text-sm font-medium text-gray-800">
                Total you are settling:{' '}
                <span className="tabular-nums text-green-700">
                  {fullCurrency(
                    (paidDisabled ? 0 : Number.parseFloat(paidAmount || '0') || 0)
                    + (toPayDisabled ? 0 : Number.parseFloat(toPayAmount || '0') || 0),
                  )}
                </span>
              </p>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
              <input
                type="text"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className={formInputClass()}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Receipt screenshot (optional)</label>
              <div className="flex flex-col items-center gap-2 border border-dashed border-gray-300 rounded-xl px-3 py-6 text-sm text-gray-600">
                <Upload className="w-4 h-4" />
                {receipt ? receipt.name : 'Upload JPG / PNG'}
                <MediaCaptureField mode="photo" onPick={onFileChange} />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                A payment image is checked by AI. With no image, the settlement is updated directly and AI review is not used.
              </p>
            </div>
          </form>
        </SettlementModalShell>
      ) : null}

      {viewDue ? (
        <CompanySettlementDueView
          kind={viewDue.kind}
          row={viewDue.row}
          canSettle={canSettle}
          onSettle={() => {
            setViewDue(null);
            openSettle();
          }}
          onViewPending={(pendingId) => openView(pendingId)}
          onClose={() => setViewDue(null)}
        />
      ) : null}

      {viewBatch ? (
        <CompanySettlementBatchView
          batch={viewBatch}
          dueHints={toPayItems}
          onClose={() => setViewBatch(null)}
        />
      ) : null}
    </DashboardLayout>
  );
};

export default AgentCompanySettlementPage;
